const DEFAULT_DEV_API_BASE_URL = '/estados-financieros';
const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const EXPECTED_GENERATOR_PROFILE = 'manual-eeff-three-sheet';
export const EXPECTED_GENERATOR_PROFILE_VERSION = '2026-07-20.exact-style-print-v2';
export const EXPECTED_SHEET_NAMES = ['BG', 'ER', 'BAL'];
export const DEFAULT_OUTPUT_FILENAME = 'estados_financieros.xlsx';

function trimTrailingSlash(value) {
  return String(value || '').replace(/\/+$/, '');
}

function getApiBaseUrl(explicitUrl) {
  const configuredUrl = explicitUrl || process.env.REACT_APP_ESTADOS_FINANCIEROS_API_URL || '';
  if (configuredUrl) return trimTrailingSlash(configuredUrl);
  return process.env.NODE_ENV === 'production' ? '' : DEFAULT_DEV_API_BASE_URL;
}

function joinEndpoint(baseUrl, endpoint) {
  const normalizedEndpoint = String(endpoint || '').startsWith('/') ? endpoint : `/${endpoint}`;
  if (!baseUrl) return normalizedEndpoint;
  return `${trimTrailingSlash(baseUrl)}${normalizedEndpoint}`;
}

export function detectAuditaloMetadataFromFilename(filename) {
  const sourceFilename = String(filename || '').trim();
  const periodMatch = sourceFilename.match(/(?:^|[_\s-])(\d{4})[_-]?(\d{2})(?=\D|$)/);
  const rfcMatch = sourceFilename.match(/([A-Z&Ñ]{3,4}\d{6}[A-Z0-9]{3})/i);
  const year = periodMatch?.[1] || '';
  const month = periodMatch?.[2] || '';

  return {
    sourceFilename,
    rfc: rfcMatch?.[1]?.toUpperCase() || '',
    periodFromFilename: year && month ? `${year}-${month}` : '',
    periodYear: year ? Number(year) : null,
    periodMonth: month ? Number(month) : null
  };
}

function getNestedValue(data, paths) {
  for (const path of paths) {
    const value = path.split('.').reduce((current, key) => {
      if (current && typeof current === 'object' && key in current) return current[key];
      return undefined;
    }, data);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '';
}

function coerceBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  if (typeof value === 'string') {
    if (value.toLowerCase() === 'true') return true;
    if (value.toLowerCase() === 'false') return false;
  }
  return fallback;
}

function normalizeWarnings(data) {
  const warnings = [
    data?.warnings,
    data?.validation?.warnings,
    data?.engine?.warnings,
    data?.workbook?.warnings,
    data?.runtime?.warnings
  ]
    .flat()
    .filter(Boolean);

  return warnings.map(warning => {
    if (typeof warning === 'string') return warning;
    return warning?.message || warning?.code || warning?.summary || JSON.stringify(warning);
  });
}

export function releaseDownloadUrl(url) {
  if (!String(url || '').startsWith('blob:')) return;
  if (typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    URL.revokeObjectURL(url);
  }
}

function normalizeMissingAccounts(data) {
  const missingAccounts = [
    data?.missing_accounts,
    data?.missingAccounts,
    data?.workbook?.missing_accounts,
    data?.workbook?.missingAccounts,
    data?.engine?.unmatched_accounts
  ]
    .flat()
    .filter(Boolean);

  return missingAccounts.map(account => {
    if (typeof account === 'string') return account;
    return account?.account_code || account?.code || account?.label || account?.account || JSON.stringify(account);
  });
}

function getDownloadUrl(data) {
  const url = getNestedValue(data, ['download_url', 'downloadUrl', 'output_url', 'outputUrl', 'archivo_salida.url']);
  if (url) return String(url);

  const outputPath = getNestedValue(data, ['output_xlsx', 'outputXlsx', 'archivo_salida', 'output.path']);
  if (typeof outputPath === 'string' && /^https?:\/\//i.test(outputPath)) return outputPath;
  if (typeof outputPath === 'string' && outputPath.startsWith('/')) return '';
  return '';
}

function createDownloadFromBase64(data) {
  const base64 = getNestedValue(data, ['output_xlsx_base64', 'outputBase64', 'archivo_salida_base64']);
  if (!base64 || typeof window === 'undefined' || typeof window.atob !== 'function') return '';

  const binary = window.atob(String(base64));
  const bytes = Uint8Array.from(binary, character => character.charCodeAt(0));
  const blob = new Blob([bytes], { type: XLSX_MIME_TYPE });
  return URL.createObjectURL(blob);
}

function normalizeSheetNames(data) {
  const value = getNestedValue(data, [
    'sheet_names',
    'sheetNames',
    'workbook.sheet_names',
    'workbook.sheetNames'
  ]);
  return Array.isArray(value) ? value.map(sheetName => String(sheetName)) : [];
}

function hasExpectedSheetNames(sheetNames) {
  return sheetNames.length === EXPECTED_SHEET_NAMES.length
    && sheetNames.every((sheetName, index) => sheetName === EXPECTED_SHEET_NAMES[index]);
}

function normalizeOutputFilename(data) {
  const value = String(getNestedValue(data, ['output_filename', 'outputFilename', 'archivo_salida.nombre']) || '').trim();
  if (!value || value.length > 180 || /[\\/\u0000-\u001F]/.test(value) || !/\.xlsx$/i.test(value)) {
    return DEFAULT_OUTPUT_FILENAME;
  }
  return value;
}

export function normalizeEstadosFinancierosResponse(data, fileMetadata = {}) {
  const periodFromFilename =
    getNestedValue(data, ['period.source_filename'])
      ? detectAuditaloMetadataFromFilename(getNestedValue(data, ['period.source_filename'])).periodFromFilename
      : fileMetadata.periodFromFilename || '';
  const periodFromContent = String(
    getNestedValue(data, ['content_period_ym', 'contentPeriodYm', 'period.content_period_ym', 'period.contentPeriodYm']) ||
      getNestedValue(data, ['period_ym', 'period.period_ym', 'period.periodYm']) ||
      ''
  );
  const outputReference = String(getNestedValue(data, ['output_xlsx', 'outputXlsx', 'archivo_salida', 'output.path']) || '');
  const downloadUrl = getDownloadUrl(data) || createDownloadFromBase64(data);
  const outputFilename = normalizeOutputFilename(data);
  const sheetNames = normalizeSheetNames(data);
  const sheetNamesMatchExpected = hasExpectedSheetNames(sheetNames);
  const missingAccounts = normalizeMissingAccounts(data);
  const warnings = normalizeWarnings(data);
  const structureIssues = [
    data?.parser?.structure_issues,
    data?.parser?.structureIssues,
    data?.structure_issues,
    data?.structureIssues
  ]
    .flat()
    .filter(Boolean);
  const normalizedRows = Number(getNestedValue(data, ['normalized_rows', 'parser.normalized_rows']) || 0);
  const formulaStaticValidation = coerceBoolean(
    getNestedValue(data, ['formula_static_validation', 'validation.formula_static_validation']),
    false
  );
  const formulaRecalculationPerformed = coerceBoolean(
    getNestedValue(data, ['formula_recalculation_performed', 'validation.formula_recalculation_performed']),
    false
  );
  const formulaRecalculationEngine = String(
    getNestedValue(data, ['formula_recalculation_engine', 'validation.formula_recalculation_engine']) || 'none'
  );
  const formulaValidationMode = String(
    getNestedValue(data, ['formula_validation_mode', 'validation.formula_validation_mode', 'runtime.formula_validation_mode'])
      || (formulaRecalculationPerformed ? 'recalculated' : 'static_only')
  );
  const evaluatedErrorValue = getNestedValue(
    data,
    ['formula_evaluated_error_count', 'validation.formula_evaluated_error_count']
  );
  const formulaEvaluatedErrorCount = evaluatedErrorValue === '' || evaluatedErrorValue === null
    ? null
    : Number(evaluatedErrorValue);
  const formulaCachedValuesAvailable = coerceBoolean(
    getNestedValue(data, ['formula_cached_values_available', 'validation.formula_cached_values_available']),
    false
  );
  const differenceCuadre = Number(getNestedValue(data, ['difference_cuadre', 'balance_check.difference_cuadre']) || 0);
  const tolerance = Number(getNestedValue(data, ['tolerance', 'balance_check.tolerance']) || 1);
  const cuadra = Boolean(getNestedValue(data, ['cuadra', 'balance_check.cuadra'])) || Math.abs(differenceCuadre) < tolerance;
  const validationOkValue = getNestedValue(data, ['validation_ok', 'validation.ok', 'ok']);
  const validationOk = validationOkValue === '' ? formulaStaticValidation && cuadra : coerceBoolean(validationOkValue);
  const backendVersion = String(getNestedValue(data, ['service_version', 'serviceVersion', 'runtime.service_version']) || '');
  const backendCommit = String(getNestedValue(data, ['git_commit', 'gitCommit', 'runtime.git_commit']) || '');
  const worktreeDirty = coerceBoolean(getNestedValue(data, ['worktree_dirty', 'worktreeDirty', 'runtime.worktree_dirty']), false);
  const processStartedAt = String(getNestedValue(data, ['process_started_at', 'processStartedAt', 'runtime.process_started_at']) || '');
  const generatorProfile = String(getNestedValue(data, ['generator_profile', 'generatorProfile', 'runtime.generator_profile']) || '');
  const generatorProfileVersion = String(
    getNestedValue(data, ['generator_profile_version', 'generatorProfileVersion', 'runtime.generator_profile_version']) || ''
  );
  const generatedAt = String(getNestedValue(data, ['generated_at', 'generatedAt', 'runtime.generated_at']) || '');
  const outputSha256 = String(getNestedValue(data, ['output_sha256', 'outputSha256', 'runtime.output_sha256']) || '');
  const runtimeWarnings = [];
  if (worktreeDirty) runtimeWarnings.push('El backend reporta un worktree sucio; su codigo puede no corresponder a un commit limpio.');
  if (!formulaRecalculationPerformed || !formulaCachedValuesAvailable) {
    runtimeWarnings.push('Las formulas solo tienen validacion estatica; no hay evidencia de recalculo evaluado.');
  }
  if (!generatorProfile || !generatorProfileVersion) {
    runtimeWarnings.push('El backend no reporto un perfil/version verificable del generador de estados financieros.');
  } else if (generatorProfile !== EXPECTED_GENERATOR_PROFILE || generatorProfileVersion !== EXPECTED_GENERATOR_PROFILE_VERSION) {
    runtimeWarnings.push(`Perfil de estados financieros anterior o inesperado: ${generatorProfile} ${generatorProfileVersion}.`);
  }
  if (!sheetNamesMatchExpected) {
    runtimeWarnings.push('El backend no reporto exactamente las hojas BG, ER y BAL requeridas.');
  }
  const allWarnings = [...runtimeWarnings, ...warnings];

  return {
    raw: data,
    status: String(data?.status || (validationOk ? 'ok' : 'warning')),
    sourceFilename: fileMetadata.sourceFilename || getNestedValue(data, ['period.source_filename']) || '',
    rfc: String(getNestedValue(data, ['rfc', 'period.rfc']) || fileMetadata.rfc || ''),
    razonSocial: String(getNestedValue(data, ['company_name', 'companyName', 'razon_social', 'razonSocial']) || ''),
    periodFromFilename,
    periodFromContent,
    periodMatches: Boolean(periodFromFilename && periodFromContent && periodFromFilename === periodFromContent),
    normalizedRows,
    structureOk: structureIssues.length === 0 && normalizedRows > 0,
    structureIssues,
    missingAccounts,
    warnings: allWarnings,
    runtimeWarnings,
    differenceCuadre,
    tolerance,
    cuadra,
    formulaStaticValidation,
    formulaRecalculationPerformed,
    formulaRecalculationEngine,
    formulaValidationMode,
    formulaEvaluatedErrorCount,
    formulaCachedValuesAvailable,
    backendVersion,
    backendCommit,
    worktreeDirty,
    processStartedAt,
    generatorProfile,
    generatorProfileVersion,
    generatedAt,
    outputSha256,
    outputSha256Short: outputSha256 ? outputSha256.slice(0, 12) : '',
    sheetNames,
    sheetNamesMatchExpected,
    outputFilename,
    validationOk,
    outputPath: '',
    downloadUrl,
    generated: Boolean(outputReference || downloadUrl || data?.output_xlsx_base64 || data?.outputBase64)
  };
}

export async function processEstadosFinancierosFile(file, options = {}) {
  if (!file) {
    throw new Error('Selecciona el archivo Auditalo antes de procesar.');
  }

  const apiBaseUrl = getApiBaseUrl(options.apiBaseUrl);
  if (!apiBaseUrl) {
    throw new Error('Configura REACT_APP_ESTADOS_FINANCIEROS_API_URL para procesar estados financieros.');
  }

  const fileMetadata = detectAuditaloMetadataFromFilename(file.name);
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(joinEndpoint(apiBaseUrl, '/process'), {
    method: 'POST',
    body: formData
  });

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes(XLSX_MIME_TYPE)) {
    if (!response.ok) {
      throw new Error(`El backend devolvio error ${response.status} al generar estados financieros.`);
    }
    const blob = await response.blob();
    return normalizeEstadosFinancierosResponse(
      {
        status: 'ok',
        output_url: URL.createObjectURL(blob)
      },
      fileMetadata
    );
  }

  const rawBody = await response.text();
  let data = null;
  try {
    data = rawBody ? JSON.parse(rawBody) : {};
  } catch {
    data = { message: rawBody };
  }

  if (!response.ok) {
    const message = data?.message || data?.error || `El backend devolvio error ${response.status}.`;
    const error = new Error(message);
    error.payload = data;
    error.status = response.status;
    throw error;
  }

  return normalizeEstadosFinancierosResponse(data, fileMetadata);
}
